import { Badge, Button, Card, EmptyState, Page, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { useMemo } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useHandlers, useSite, withPageProviders } from '../../../context/page-context';
import { trackUpgradeClicked } from '../../../lib/mixpanel';
import { useCustomerRoles } from '../hooks/use-customer-roles';
import { useSalesAgentsAvailability } from '../hooks/use-sales-agent-availability';
import type { CustomerRoleWithAssignedCount } from '../types/role';

const ROLES_LEARN_MORE_URL = 'https://docs.sparklayer.io/sales-agent-ordering#sales-agent-roles';

export const RolesPage = withPageProviders(RolesPageContent);

function RolesPageContent() {
  const { navigationHandler } = useHandlers();
  const site = useSite();
  const { data: roles = [], isLoading, isError, refetch } = useCustomerRoles();
  const { data: salesAgents } = useSalesAgentsAvailability();

  const isRolesEnabled: boolean = salesAgents !== undefined && salesAgents.max !== 0;

  const { salesRoles } = useMemo(() => {
    const sales: CustomerRoleWithAssignedCount[] = [];
    const company: CustomerRoleWithAssignedCount[] = [];
    for (const role of roles) {
      if (role.type === 'sales') {
        sales.push(role);
      } else {
        company.push(role);
      }
    }
    sales.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
    return { salesRoles: sales, companyRoles: company };
  }, [roles]);

  const showEmptyState = (roles.length === 0 && !isLoading && isRolesEnabled) || !isRolesEnabled;

  const pagePrimaryAction = {
    label: t('features:customers.pages.roles_list_page.create_button'),
    onClick: () => navigationHandler.navigate('/customers/roles/create'),
  };

  return (
    <Page
      title={t('features:customers.pages.roles_list_page.title')}
      description={t('features:customers.pages.roles_list_page.description')}
      learnMoreUrl={ROLES_LEARN_MORE_URL}
      footerDescription={t('features:customers.pages.roles_list_page.footer')}
      primaryAction={isRolesEnabled ? pagePrimaryAction : undefined}
    >
      {showEmptyState && (
        <Card>
          <EmptyState
            title={t('features:customers.pages.roles_list_page.title')}
            imageType="customerGroup"
            subtitle={t('features:customers.pages.roles_list_page.description')}
          >
            <div className="flex flex-row items-center gap-3">
              <Button
                onClick={() => {
                  trackUpgradeClicked(site, 'Roles');
                  navigationHandler.navigateById('plan');
                }}
              >
                {t('buttons:upgrade')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.open(ROLES_LEARN_MORE_URL, '_blank')}
              >
                {t('links:learn_more')}
              </Button>
            </div>
          </EmptyState>
        </Card>
      )}

      {!showEmptyState && (
        <SparkSuspense
          isLoading={isLoading}
          isError={isError}
          errorMessage={t('errors:generic')}
          onReload={() => void refetch()}
        >
          <div className="flex flex-col gap-8">
            {salesRoles.length > 0 && (
              <RolesTableSection
                title={t('features:customers.pages.roles_list_page.sections.sales_rep_roles')}
                roles={salesRoles}
                assignedLabelKey="agents"
              />
            )}
            {/* TODO: Below to be added when company user roles are added */}
            {/* {companyRoles.length > 0 && (
              <RolesTableSection
                title={t('features:customers.pages.roles_list_page.sections.company_roles')}
                roles={companyRoles}
                assignedLabelKey="customers"
              />
            )} */}
          </div>
        </SparkSuspense>
      )}
    </Page>
  );
}

function RolesTableSection({
  title,
  roles,
  // TODO: Below to be added when company user roles are added
  // assignedLabelKey,
}: {
  title: string;
  roles: CustomerRoleWithAssignedCount[];
  assignedLabelKey: 'agents' | 'customers';
}) {
  const { navigationHandler } = useHandlers();

  const headings = [
    t('features:customers.pages.roles_list_page.table.headings.name'),
    t('features:customers.pages.roles_list_page.table.headings.type'),
    t('features:customers.pages.roles_list_page.table.headings.assigned'),
    '',
  ];

  return (
    <Table caption={title} headings={headings}>
      {roles.map((role, i) => (
        <Table.Row key={role.id} id={role.id} index={i}>
          <Table.Cell>
            <Text>{role.name}</Text>
          </Table.Cell>
          <Table.Cell>
            <Badge variant={role.is_default ? 'yellow' : 'blue'}>
              {role.is_default
                ? t('features:customers.pages.roles_list_page.table.type_system')
                : t('features:customers.pages.roles_list_page.table.type_custom')}
            </Badge>
          </Table.Cell>
          <Table.Cell>
            <Text>
              {role.assigned_customer_count}{' '}
              {role.assigned_customer_count === 1 ? 'agent' : 'agents'}
            </Text>
          </Table.Cell>
          <Table.Cell sticky>
            <Button
              variant="secondary"
              disabled={role.is_default === true}
              onClick={() => {
                if (!role.is_default) {
                  navigationHandler.navigate(
                    `/customers/roles/edit/${encodeURIComponent(role.slug)}`,
                  );
                }
              }}
            >
              {t('buttons:edit')}
            </Button>
          </Table.Cell>
        </Table.Row>
      ))}
    </Table>
  );
}
